// Modifier Sequencing Engine
// Rule-based modifier application with explainable reasoning

import { MODIFIER_DESCRIPTIONS, type SiteOfService, type Laterality } from './types';
import { getCptTag } from './cptTagMap';
import { getFilteredModifiers, getAutoLateralityModifier } from './filterModifiers';
import { getDistinctServiceModifier, isXepsuModifier } from './xepsu';

export interface ModifierApplication {
  modifier: string;
  reason: string;
  ruleSource: 'laterality' | 'payer_rule' | 'documentation' | 'site_of_service' | 'manual';
  confidence: 'high' | 'medium' | 'low';
}

export interface ModifierSequencingResult {
  cptCode: string;
  appliedModifiers: ModifierApplication[];
  finalCodeString: string;
  modifierExplanations: Record<string, string>;
  payerSpecificRulesApplied: boolean;
  matchedRuleIds: string[];
  warnings: string[];
}

export interface ModifierSequencingInput {
  cptCode: string;
  siteOfService: SiteOfService | null;
  laterality: Laterality | null;
  payerId?: string;
  planId?: string;
  documentationText?: string;
  existingModifiers?: string[];
  /** 'primary' or 'secondary' — drives distinct-service and -51 logic */
  section?: 'primary' | 'secondary';
  /** Whether multiple primary procedures exist in the case (drives -51) */
  hasMultiplePrimaries?: boolean;
  /** Anatomic site identifier (e.g., 'knee', 'shoulder', 'elbow') for multi-site logic */
  anatomicSite?: string;
  /** All anatomic sites in the case — triggers multi-site modifier rules when >1 */
  allAnatomicSites?: string[];
}

// Distinct procedural service detection keywords
const DISTINCT_SERVICE_KEYWORDS = [
  'separate anatomic site',
  'separate incision',
  'separate compartment',
  'different anatomic structure',
  'distinct procedure',
  'separate procedure',
  'additional compartment',
  'separate lesion',
  'different compartment',
  'separate structure',
];

// XS/XE/XP/XU modifier keywords
const MODIFIER_XS_KEYWORDS = [
  'separate structure',
  'separate anatomic structure',
  'different anatomic site',
  'separate organ',
  'distinct anatomic site',
];

// Multiple-procedure keywords (for auto -51)
const MULTIPLE_PROCEDURE_KEYWORDS = [
  'concurrent',
  'additional procedure',
  'multiple procedures',
  'same session',
  'same operative session',
];

/**
 * Apply modifiers to a confirmed CPT based on rules and documentation
 */
export function applyModifiers(input: ModifierSequencingInput): ModifierSequencingResult {
  const appliedModifiers: ModifierApplication[] = [];
  const warnings: string[] = [];
  const matchedRuleIds: string[] = [];
  let payerSpecificRulesApplied = false;

  const cptTag = getCptTag(input.cptCode, input.siteOfService || undefined);
  const filterResult = getFilteredModifiers(input.cptCode, input.siteOfService, input.laterality);

  // 1. Apply laterality modifier if required and selected
  if (input.laterality && cptTag.laterality_required) {
    const lateralityMod = getAutoLateralityModifier(input.laterality);
    if (lateralityMod) {
      appliedModifiers.push({
        modifier: lateralityMod,
        reason: getLateralityReason(lateralityMod),
        ruleSource: 'laterality',
        confidence: 'high',
      });
    }
  }

  // 2. Include any existing modifiers that are allowed
  if (input.existingModifiers) {
    for (const mod of input.existingModifiers) {
      // Skip if already applied (e.g., laterality)
      if (appliedModifiers.some(m => m.modifier === mod)) continue;
      
      // Validate against allowed modifiers
      if (filterResult.allowedModifiers.includes(mod) || 
          filterResult.lateralityModifiers.includes(mod) ||
          filterResult.nonLateralityModifiers.includes(mod)) {
        appliedModifiers.push({
          modifier: mod,
          reason: MODIFIER_DESCRIPTIONS[mod] || `Modifier ${mod} applied per case selection`,
          ruleSource: 'manual',
          confidence: 'medium',
        });
      } else {
        warnings.push(`Modifier ${mod} not allowed for this CPT/site combination`);
      }
    }
  }

  // 3. Auto-apply the specific CMS X{EPSU} modifier for secondary/ancillary procedures.
  if (input.section === 'secondary' && !appliedModifiers.some(m => m.modifier === '59' || isXepsuModifier(m.modifier))) {
    const modifier = getDistinctServiceModifier('separate_structure');
    appliedModifiers.push({
      modifier,
      reason: 'Separate structure — secondary/ancillary procedure documented as distinct from the primary service',
      ruleSource: 'payer_rule',
      confidence: 'high',
    });
    matchedRuleIds.push('auto_xs_secondary');
  }

  // 4. Auto-apply -51 for additional primary procedures (multiple procedure reduction)
  if (input.section === 'primary' && input.hasMultiplePrimaries && !appliedModifiers.some(m => m.modifier === '51')) {
    appliedModifiers.push({
      modifier: '51',
      reason: 'Multiple procedures — additional primary procedure in same operative session',
      ruleSource: 'payer_rule',
      confidence: 'high',
    });
    matchedRuleIds.push('auto_51_multiple_primary');
  }

  // 4b. MULTI-SITE / MULTI-JOINT: Auto-apply XS or -51 for procedures on distinct anatomic sites
  if (input.anatomicSite && input.allAnatomicSites && input.allAnatomicSites.length > 1) {
    const isSecondarySite = input.allAnatomicSites.indexOf(input.anatomicSite) > 0;
    if (isSecondarySite && !appliedModifiers.some(m => m.modifier === '59' || isXepsuModifier(m.modifier))) {
      const modifier = getDistinctServiceModifier('separate_structure');
      appliedModifiers.push({
        modifier,
        reason: `Separate structure — procedure on secondary anatomic site (${input.anatomicSite}) distinct from primary site (${input.allAnatomicSites[0]}). Required to prevent NCCI bundling denial.`,
        ruleSource: 'payer_rule',
        confidence: 'high',
      });
      matchedRuleIds.push('auto_xs_multi_site');
    }
    if (isSecondarySite && input.section === 'primary' && !appliedModifiers.some(m => m.modifier === '51')) {
      appliedModifiers.push({
        modifier: '51',
        reason: `Multiple procedures — primary procedure on secondary anatomic site (${input.anatomicSite}) in same operative session`,
        ruleSource: 'payer_rule',
        confidence: 'high',
      });
      matchedRuleIds.push('auto_51_multi_site');
    }
  }

  if (input.documentationText) {
    const docLower = input.documentationText.toLowerCase();
    
    // Check for distinct-service modifier applicability
    const needsDistinctServiceModifier = DISTINCT_SERVICE_KEYWORDS.some(keyword =>
      docLower.includes(keyword.toLowerCase())
    );
    
    if (needsDistinctServiceModifier && !appliedModifiers.some(m => isXepsuModifier(m.modifier) || m.modifier === '59')) {
      const modifier = getDistinctServiceModifier('separate_structure');
      if (filterResult.allowedModifiers.includes(modifier) ||
          filterResult.nonLateralityModifiers.includes(modifier)) {
        const matchedKeyword = DISTINCT_SERVICE_KEYWORDS.find(k => 
          docLower.includes(k.toLowerCase())
        );
        appliedModifiers.push({
          modifier,
          reason: `Separate structure: "${matchedKeyword}" found in documentation`,
          ruleSource: 'documentation',
          confidence: 'medium',
        });
      }
    }

    // Check for XS modifier applicability (more specific CMS alternative to -59)
    const needsXS = MODIFIER_XS_KEYWORDS.some(keyword => 
      docLower.includes(keyword.toLowerCase())
    );
    
    if (needsXS && !appliedModifiers.some(m => m.modifier === 'XS' || m.modifier === '59')) {
      const matchedKeyword = MODIFIER_XS_KEYWORDS.find(k => 
        docLower.includes(k.toLowerCase())
      );
      warnings.push(`Documentation supports XS: "${matchedKeyword}" suggests separate anatomic structure.`);
    }

    // Check for -51 from documentation keywords
    const needs51 = MULTIPLE_PROCEDURE_KEYWORDS.some(keyword =>
      docLower.includes(keyword.toLowerCase())
    );
    if (needs51 && !appliedModifiers.some(m => m.modifier === '51')) {
      warnings.push('Documentation suggests multiple procedures — consider -51 modifier');
    }
  }

  // 6. Apply site of service specific rules
  if (cptTag.category === 'IMAGING' && input.siteOfService) {
    if (input.siteOfService === 'hospital' || input.siteOfService === 'asc') {
      if (!appliedModifiers.some(m => m.modifier === '26' || m.modifier === 'TC')) {
        if (filterResult.allowedModifiers.includes('26')) {
          appliedModifiers.push({
            modifier: '26',
            reason: 'Professional component only - facility billing technical component separately',
            ruleSource: 'site_of_service',
            confidence: 'high',
          });
        }
      }
    }
  }

  // 7. Check for payer-specific rules (placeholder for integration)
  if (input.payerId) {
    payerSpecificRulesApplied = false; // Will be true when payer rules are matched
  }

  // Build final code string
  const modifierCodes = appliedModifiers.map(m => m.modifier);
  const finalCodeString = buildCodeString(input.cptCode, modifierCodes);
  
  // Build explanations object
  const modifierExplanations: Record<string, string> = {};
  for (const mod of appliedModifiers) {
    modifierExplanations[mod.modifier] = mod.reason;
  }

  return {
    cptCode: input.cptCode,
    appliedModifiers,
    finalCodeString,
    modifierExplanations,
    payerSpecificRulesApplied,
    matchedRuleIds,
    warnings,
  };
}

/**
 * Build code string in proper format: CPT-MOD1-MOD2
 */
export function buildCodeString(cptCode: string, modifiers: string[]): string {
  if (modifiers.length === 0) {
    return cptCode;
  }
  // Sort modifiers: laterality first, then numeric, then alphabetic
  const sorted = sortModifiers(modifiers);
  return `${cptCode}-${sorted.join('-')}`;
}

/**
 * Sort modifiers in proper sequence
 * Order: Laterality (RT, LT, 50) → Numeric (ascending) → Alphabetic
 */
function sortModifiers(modifiers: string[]): string[] {
  const laterality = ['RT', 'LT', '50'];
  const latMods = modifiers.filter(m => laterality.includes(m));
  const numMods = modifiers.filter(m => !laterality.includes(m) && /^\d+$/.test(m));
  const alphaMods = modifiers.filter(m => !laterality.includes(m) && !/^\d+$/.test(m));
  
  return [
    ...latMods.sort(),
    ...numMods.sort((a, b) => parseInt(a) - parseInt(b)),
    ...alphaMods.sort(),
  ];
}

/**
 * Get explanation for laterality modifier
 */
function getLateralityReason(modifier: string): string {
  switch (modifier) {
    case 'RT': return 'Right side procedure per surgeon selection';
    case 'LT': return 'Left side procedure per surgeon selection';
    case '50': return 'Bilateral procedure per surgeon selection';
    default: return 'Laterality modifier applied';
  }
}

/**
 * Validate modifier sequence for compliance
 */
export function validateModifierSequence(
  cptCode: string,
  modifiers: string[],
  siteOfService: SiteOfService | null
): { isValid: boolean; errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // Check for conflicting laterality
  const hasRT = modifiers.includes('RT');
  const hasLT = modifiers.includes('LT');
  const has50 = modifiers.includes('50');
  
  if ((hasRT && hasLT) || (hasRT && has50) || (hasLT && has50)) {
    errors.push('Conflicting laterality modifiers. Use only one: RT, LT, or 50');
  }
  
  // Check for 26 + TC conflict
  if (modifiers.includes('26') && modifiers.includes('TC')) {
    errors.push('Cannot use both 26 (Professional) and TC (Technical) modifiers');
  }
  
  // Check for duplicate modifiers
  const uniqueMods = new Set(modifiers);
  if (uniqueMods.size !== modifiers.length) {
    errors.push('Duplicate modifiers detected');
  }
  
  // Warn about modifier 59 usage
  if (modifiers.includes('59')) {
    warnings.push('Modifier 59 is a legacy fallback. Prefer XE, XS, XP, or XU when the payer supports a more specific distinct-service modifier.');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Demo mode: Auto-apply typical modifiers for demo cases
 */
export function applyDemoModifiers(input: ModifierSequencingInput): ModifierSequencingResult {
  // Start with normal application
  const result = applyModifiers(input);
  
  // In demo mode, mark all as high confidence
  result.appliedModifiers = result.appliedModifiers.map(m => ({
    ...m,
    confidence: 'high' as const,
  }));
  
  // Clear warnings for demo
  result.warnings = [];
  
  return result;
}
